import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { shipmentMissingService } from "../../services/shipmentMissingService"
import { shipmentService } from "../../services/shipmentService"
import { DashboardLayout } from '../common/page_component/index.js'
import Alert from "../common/Alert"
import Button from '../common/Button.js'
import { AlertTriangle, ArrowLeft } from "lucide-react";
import { validators, inputFilters, useValidation, ValidationMessage } from "../common/Validation.js";
import ListPageHeader from '../common/ListPageHeader.js';
import { formInputClass, formLabelClass, formTextareaClass } from '../common/uiClasses.js';


const AddMissing = () => {
  const navigate = useNavigate()
  const { errors, validate, validateField } = useValidation()

  const validationRules = {
    shipment_id: (v) => validators.required(v, 'Shipment ID'),
    reason:      [(v) => validators.required(v, 'Reason'), (v) => v && v.trim().length < 10 ? 'Reason must be at least 10 characters' : ''],
  }

  const [loading, setLoading] = useState(false)
  const [searchLoading, setSearchLoading] = useState(false)
  const [shipmentDetails, setShipmentDetails] = useState(null)
  const [formData, setFormData] = useState({
    shipment_id: "",
    reason: "",
    missing_details: "",
    quantity_missing: "",
  })

  const handleShipmentSearch = async (e) => {
    const shipmentId = e.target.value
    setFormData({ ...formData, shipment_id: shipmentId })
    setShipmentDetails(null)
    validateField('shipment_id', shipmentId, validationRules.shipment_id)

    if (shipmentId.trim().length === 0) {
      return
    }

    try {
      setSearchLoading(true)
      const response = await shipmentService.getShipment(shipmentId)

      const shipment = response.data.shipment || response.data.data

      if (shipment) {
        setShipmentDetails(shipment)
      } else {
        setShipmentDetails(null)
        Alert.error("Shipment not found")
      }
    } catch (error) {
      setShipmentDetails(null)
      console.error("Shipment search error:", error)

      if (error.response?.status === 404) {
        Alert.error("Shipment not found")
      } else if (error.response?.data?.error) {
        Alert.error(error.response.data.error)
      } else {
        Alert.error("Error searching for shipment. Please try again.")
      }
    } finally {
      setSearchLoading(false)
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    let filtered = value
    if (name === 'quantity_missing') filtered = inputFilters.digitsOnly(value)
    setFormData({ ...formData, [name]: filtered })

    if (validationRules[name]) {
      validateField(name, filtered, validationRules[name])
    }
  }

  const validateForm = () => {
    if (!validate(formData, validationRules)) return false

    if (!shipmentDetails) {
      Alert.error("Please search and select a valid shipment")
      return false
    }

    return true
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    const confirmed = await Alert.confirm(
      `Report missing items for shipment ${formData.shipment_id}?`,
      "Confirm Missing Report",
    )

    if (!confirmed) return

    try {
      setLoading(true)

      const submitData = {
        shipment_id: formData.shipment_id.trim(),
        reason: formData.reason.trim(),
        missing_details: formData.missing_details.trim(),
        quantity_missing: formData.quantity_missing ? Number.parseInt(formData.quantity_missing) : null,
      }

      await shipmentMissingService.createMissing(submitData)

      Alert.success("Missing shipment report created successfully!")
      navigate("/missing-shipments")
    } catch (error) {
      console.error("Error creating missing report:", error)

      if (error.response?.data?.error) {
        Alert.error(error.response.data.error)
      } else {
        Alert.error("Failed to create missing report. Please try again.")
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto">
        <ListPageHeader title="Report Missing Shipment" icon={AlertTriangle} backTo="/missing-shipments">
          <Button variant="secondary" icon={ArrowLeft} type="button" onClick={() => navigate("/missing-shipments")}>
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            form="add-missing-form"
            loading={loading}
            disabled={loading || !shipmentDetails || formData.reason.trim().length < 10}
          >
            {loading ? "Submitting Report..." : "Submit Missing Report"}
          </Button>
        </ListPageHeader>

        <form id="add-missing-form" onSubmit={handleSubmit}>
          <div className="bg-white rounded-xl shadow-md p-4 sm:p-5 mb-4 sm:mb-6">
            <h2 className="text-base sm:text-md font-semibold text-secondary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
              Shipment Selection
            </h2>
            <div className="mb-4">
              <label className={formLabelClass}>
                Shipment ID <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="shipment_id"
                value={formData.shipment_id}
                onChange={handleShipmentSearch}
                placeholder="Enter shipment ID to search (e.g., 123456)"
                className={formInputClass(Boolean(errors.shipment_id))}
                required
                disabled={loading}
              />
              <ValidationMessage error={errors.shipment_id} />
              {searchLoading && (
                <div className="mt-2 flex items-center gap-2 text-blue-600 text-sm">
                  <div className="animate-spin rounded-full h-4 w-4 border border-blue-600 border-t-transparent"></div>
                  <span>Searching for shipment...</span>
                </div>
              )}
            </div>

            {shipmentDetails && (
              <div className="bg-blue-50 border border-blue-100 p-4 sm:p-5 rounded-xl">
                <div className="flex items-center gap-2 mb-4">
                  <h3 className="text-base font-semibold text-gray-800">Shipment Found</h3>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-2 gap-3 sm:gap-4 bg-white p-4 sm:p-5 rounded-xl border border-gray-100">
                  <div>
                    <p className="text-xs sm:text-sm text-gray-600 font-semibold">Shipment ID</p>
                    <p className="font-medium text-gray-700 text-sm sm:text-base bg-gray-100 p-2 py-1 mt-1">
                      #{shipmentDetails.shipment_id}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs sm:text-sm text-gray-600 font-semibold">Status</p>
                    <p className="font-medium text-gray-700 text-sm sm:text-base bg-gray-100 p-2 py-1 mt-1">
                      {shipmentDetails.status}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs sm:text-sm text-gray-600 font-semibold">Sender Name</p>
                    <p className="font-medium text-gray-700 text-sm sm:text-base bg-gray-100 p-2 py-1 mt-1">{shipmentDetails.sender_name}</p>
                  </div>
                  <div>
                    <p className="text-xs sm:text-sm text-gray-600 font-semibold">Receiver Name</p>
                    <p className="font-medium text-gray-700 text-sm sm:text-base bg-gray-100 p-2 py-1 mt-1">{shipmentDetails.receiver_name}</p>
                  </div>
                  <div className="sm:col-span-2">
                    <p className="text-xs sm:text-sm text-gray-600 font-semibold">Delivery Address</p>
                    <p className="font-medium text-gray-700 text-sm sm:text-base bg-gray-100 p-2 py-1 mt-1">{shipmentDetails.receiver_address}</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="bg-white rounded-xl shadow-md p-4 sm:p-5 mb-4 sm:mb-6">
            <h3 className="text-base sm:text-md font-semibold text-secondary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
              Missing Item Details
            </h3>

            <div className="space-y-5">
              <div>
                <label className={formLabelClass}>
                  Reason <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="reason"
                  value={formData.reason}
                  onChange={handleChange}
                  placeholder="Describe why items are missing or damaged (e.g., items not found during delivery, damaged in transit, quantity mismatch, etc.)"
                  rows="4"
                  className={formTextareaClass(Boolean(errors.reason))}
                  required
                  disabled={loading}
                />
                <ValidationMessage error={errors.reason} />
                <div className="flex items-center justify-between mt-2 text-xs">
                  <span
                    className={`font-semibold ${formData.reason.length < 10 ? "text-red-600" : "text-green-600"}`}
                  >
                    {formData.reason.length} / 10 characters minimum
                  </span>
                </div>
              </div>

              <div>
                <label className={formLabelClass}>Detailed Description</label>
                <textarea
                  name="missing_details"
                  value={formData.missing_details}
                  onChange={handleChange}
                  placeholder="Additional details about the missing or damaged items"
                  rows="3"
                  className={formTextareaClass()}
                  disabled={loading}
                />
              </div>

              <div>
                <label className={formLabelClass}>Quantity Missing (Optional)</label>
                <input
                  type="text"
                  name="quantity_missing"
                  value={formData.quantity_missing}
                  onChange={handleChange}
                  placeholder="Number of items missing"
                  className={formInputClass()}
                  disabled={loading}
                />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md p-4 sm:p-5 flex flex-col sm:flex-row gap-3 justify-end">
            <Button variant="secondary" type="button" onClick={() => navigate("/missing-shipments")} disabled={loading}>
              Cancel
            </Button>
            <Button
              variant="primary"
              type="submit"
              loading={loading}
              disabled={loading || !shipmentDetails || formData.reason.trim().length < 10}
            >
              {loading ? "Submitting Report..." : "Submit Missing Report"}
            </Button>
          </div>
        </form>
      </div>
    </DashboardLayout>
  )
}

export default AddMissing;
